import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import useScreen from 'Hooks/useScreen';
import { ReactComponent as CloseIcon } from 'assets/images/close-black.svg';
import Colors from "modules/Colors";
import { clsx, styx } from "modules/Utils";
import { createPortal } from 'react-dom';
import Sty from 'modules/Sty';
import ButtonSymphony, { ButtonColor, ButtonVariant } from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';

const MODAL_ANIMATION_DURATION = 200;

export interface ModalSymphonyProps {
    className: string;
    style?: CSSProperties;
    styleModalContent?: CSSProperties;
    styleLeftSideContent?: CSSProperties;
    children?: ReactNode;
    isOpen: boolean;
    onClose: () => void;
    title?: string | ReactNode;
    subtitle?: string | ReactNode;
    headerIcon?: ReactNode | string | SVGElement | null | undefined;
    fullScreen?: boolean;
    maxWidth?: number | string;
    titleStyle?: CSSProperties;
    subtitleStyle?: CSSProperties;
    size?: 'small' | 'medium' | 'large'; // 360 | 678 | 1055
    isIsolateModalContent?: boolean;
    isHideCloseButton?: boolean;

    onClickButtonLeft?: () => void;
    buttonLeftText?: string;
    buttonLeftVariant?: ButtonVariant;
    buttonLeftDisabled?: boolean;
    buttonLeftColor?: ButtonColor;
    buttonLeftIsLoading?: boolean;
    buttonLeftLoadingText?: string;
    buttonLeftClassName?: string;

    onClickButtonRight?: () => void;
    buttonRightText?: string;
    buttonRightVariant?: ButtonVariant;
    buttonRightDisabled?: boolean;
    buttonRightColor?: ButtonColor;
    buttonRightIsLoading?: boolean;
    buttonRightLoadingText?: string;
    buttonRightClassName?: string;

    isRed?: boolean;
    isGreen?: boolean;

    leftSideContent?: ReactNode;
}

const ModalSymphony = ({
    className = '',
    style: styleProp = {},
    styleModalContent = {},
    styleLeftSideContent = {},
    children = null,
    isOpen = false,
    onClose = () => { },
    title = '',
    subtitle = '',
    headerIcon = null,
    fullScreen = false,
    maxWidth,
    titleStyle = {},
    subtitleStyle = {},
    size = 'medium',
    isIsolateModalContent = false,
    isHideCloseButton = false,

    onClickButtonLeft,
    buttonLeftText = '',
    buttonLeftDisabled = false,
    buttonLeftColor = 'grey',
    buttonLeftVariant = 'text',
    buttonLeftIsLoading = false,
    buttonLeftLoadingText = '',
    buttonLeftClassName = '',

    onClickButtonRight,
    buttonRightText = '',
    buttonRightDisabled = false,
    buttonRightColor = 'purple',
    buttonRightVariant = undefined,
    buttonRightIsLoading = false,
    buttonRightLoadingText = '',
    buttonRightClassName = '',

    isRed = false,
    isGreen = false,

    leftSideContent = null,
}: ModalSymphonyProps): JSX.Element | null => {
    const { mStyle, cssCalc } = useScreen();
    /** Tracks the closing animation state */
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const handleEscKey = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    beforeClose();
                }
            };
            
            document.addEventListener('keydown', handleEscKey);
            
            return () => {
                document.removeEventListener('keydown', handleEscKey);
            };
        }
    }, [isOpen]);

    const beforeClose = () => {
        setIsClosing(true);
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, MODAL_ANIMATION_DURATION); // Match animation duration
    }

    const renderCloseIcon = () => {
        if (isHideCloseButton) {
            return null;
        }

        return (
            <div
                className="closeIconContainer"
                style={mStyle(sty, 'closeIconContainer')}
                onClick={() => beforeClose()}
            >
                <CloseIcon style={mStyle(sty, 'closeIconSvg')} />
            </div>
        )
    }

    const renderHeaderIcon = () => {
        if (!headerIcon) {
            return null;
        }

        if (headerIcon instanceof SVGElement) {
            return (
                <div
                    className="modalSymphonyHeaderIcon"
                    style={sty.modalSymphonyHeaderIcon}
                    dangerouslySetInnerHTML={{ __html: headerIcon.outerHTML }}
                />
            )
        }

        if (typeof headerIcon === 'string') {
            return (
                <Icon
                    className="modalSymphonyHeaderIcon"
                    style={sty.modalSymphonyHeaderIcon}
                    icon={headerIcon}
                />
            )
            // return (
            //     <img
            //         className="modalSymphonyHeaderIcon"
            //         style={sty.modalSymphonyHeaderIcon}
            //         src={headerIcon} 
            //         alt="modal header"
            //     />
            // )
        }

        return (
            <div
                className="modalSymphonyHeaderIcon"
                style={sty.modalSymphonyHeaderIcon}
            >
                {headerIcon}
            </div>
        )
    }

    const renderTitles = () => {
        return (
            <>
                {title &&
                    <div
                        className="modalSymphonyTitle"
                        style={styx(
                            sty.modalSymphonyTitle,
                            sty[`modalSymphonyTitle_${size}`],
                            titleStyle
                        )}
                    >
                        {title}
                    </div>
                }
                {subtitle &&
                    <div
                        className="modalSymphonySubtitle"
                        style={styx(
                            sty.modalSymphonySubtitle,
                            sty[`modalSymphonySubtitle_${size}`],
                            isIsolateModalContent ? { ...subtitleStyle, marginBottom: 0 } : subtitleStyle
                        )}
                    >
                        {subtitle}
                    </div>
                }
            </>
        )
    }

    const renderModalHeader = () => {
        const headerContent = (
            <>
                {renderCloseIcon()}
                {renderHeaderIcon()}
                {renderTitles()}
            </>
        )

        if (isIsolateModalContent) {
            return (
                <div className="titleContainer" style={mStyle(sty, 'titleContainer')}>
                    {headerContent}
                </div>
            )
        }

        return headerContent;
    }

    const renderModalChildren = () => {
        return (
            <div
                className="modalSymphonyChildren"
                style={styx(sty.modalSymphonyChildren, styleProp)}
            >
                {children}
                {(onClickButtonLeft || onClickButtonRight) &&
                    <div className="modalSymphonyButtonsContainer" style={sty.modalSymphonyButtonsContainer}>
                        {onClickButtonLeft &&
                            <ButtonSymphony
                                variant={buttonLeftVariant ? buttonLeftVariant : 'text'}
                                className={clsx('modalSymphonyButtonLeft', buttonLeftClassName)}
                                onClick={onClickButtonLeft}
                                color={buttonLeftColor ? buttonLeftColor as ButtonColor : isRed ? 'red' : 'grey'}
                                disabled={buttonLeftDisabled}
                                isLoading={buttonLeftIsLoading}
                                loadingText={buttonLeftLoadingText}
                            >
                                {buttonLeftText}
                            </ButtonSymphony>
                        }
                        {onClickButtonRight &&
                            <ButtonSymphony
                                variant={buttonRightVariant ? buttonRightVariant : undefined}
                                className={clsx('modalSymphonyButtonRight', buttonRightClassName)}
                                onClick={onClickButtonRight}
                                color={buttonRightColor ? buttonRightColor as ButtonColor : isGreen ? 'green' : 'purple'}
                                disabled={buttonRightDisabled}
                                isLoading={buttonRightIsLoading}
                                loadingText={buttonRightLoadingText}
                            >
                                {buttonRightText}
                            </ButtonSymphony>
                        }
                    </div>
                }
            </div>
        )
    }

    if (!isOpen) {
        return null;
    }

    const modalContentComponent = (
        <div
            className="modalSymphonyContent"
            style={styx(
                mStyle(sty, 'modalSymphonyContent'),
                !leftSideContent && maxWidth ? { maxWidth } : sty[size],
                isIsolateModalContent ? sty.isolateModalContent : {},
                fullScreen ? sty.fullScreenContentStyle : {},
                sty.contentAnimation,
                isClosing && sty.contentFadeOutAnimation,
                leftSideContent ? sty.modalSymphonyContent_leftSideContent : {},
                styleModalContent
            )}
        >
            {renderModalHeader()}
            {renderModalChildren()}
        </div>
    )

    // NOTE: render()
    return createPortal(
        <div
            className={clsx('modalSymphonyElement', className)}
            style={styx(
                mStyle(sty, 'modalSymphonyElement'),
                { height: cssCalc() },
                fullScreen && sty.fullScreenComponentStyle,
                sty.fadeAnimation,
                isClosing && sty.fadeOutAnimation
            )}
        >
            {leftSideContent ?
                <div
                    className="leftSideContentContainer"
                    style={styx(
                        sty.leftSideContentContainer,
                        maxWidth ? { maxWidth } : sty[size],
                        isClosing && sty.contentFadeOutAnimation,
                    )}
                >
                    <div
                        className="modalSymphonyLeftSideContent"
                        style={styx(
                            sty.modalSymphonyLeftSideContent,
                            styleLeftSideContent
                        )}
                    >
                        {leftSideContent}
                    </div>
                    {modalContentComponent}
                </div>
                :
                modalContentComponent
            }
        </div>,
        document.body
    );
};

const sty: Record<string, CSSProperties> = {
    modalSymphonyElement: {
        position: "fixed",
        display: "flex",
        margin: "auto",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        padding: "32px 10px 32px 10px",
        zIndex: 10000,
        width: '100%',
        overscrollBehavior: "none",
        left: 0,
        top: 0,
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 16,
        paddingRight: 16,
    },
    modalSymphonyElement_mobile: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 8,
        paddingRight: 8,
    },
    modalSymphonyContent: {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        position: "relative",
        border: "none",
        width: "100%",
        borderTopLeftRadius: 18,
        borderBottomLeftRadius: 18,
        borderTopRightRadius: 18,
        borderBottomRightRadius: 18,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        backgroundColor: "white",
        outline: "none",
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 32,
        paddingRight: 32,
        // overflow: 'hidden',
    },
    modalSymphonyContent_mobile: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        height: 'auto',
        minHeight: 'auto',
    },
    modalSymphonyContent_leftSideContent: {
        marginLeft: 0,
        marginTop: 0,
        marginBottom: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
    modalSymphonyHeaderIcon: {
        alignSelf: "flex-start",
        width: 40,
        height: 40,
        marginBottom: 8,
    },
    modalSymphonyTitle: {
        alignSelf: "flex-start",
        marginBottom: 8,
        ...Sty.text24bold,
    },
    modalSymphonySubtitle: {
        alignSelf: "flex-start",
        marginBottom: 24,
        width: '100%',
        ...Sty.text20grey,
    },
    modalSymphonyTitle_small: {
        ...Sty.text20,
    },
    modalSymphonySubtitle_small: {
        ...Sty.text16grey,
    },
    small: {
        maxWidth: 360,
        paddingLeft: 24,
        paddingRight: 24,
    },
    medium: {
        maxWidth: 678,
    },
    large: {
        maxWidth: 1055,
    },

    closeIconContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        width: 40,
        height: 40,
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        borderRadius: 9999,
        cursor: "pointer",
        alignSelf: "flex-end",
        flexShrink: 0,
        position: "unset",
        marginTop: -4,
        marginRight: -12,
    },
    closeIconContainer_mobile: {
        marginTop: 0,
        marginRight: 0,
        width: 32,
        height: 32,
    },
    closeIconSvg: {
        width: 24,
        height: 24,
    },
    closeIconSvg_mobile: {
        width: 19,
        height: 19,
    },
    fullScreenComponentStyle: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        backgroundColor: 'white',
    },
    fullScreenContentStyle: {
        width: '100%',
        maxWidth: '100%',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        height: '100%',
        marginTop: 0,
        overflowY: 'scroll',
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 32,
        paddingRight: 32,
        width: '100%',
    },
    titleContainer_mobile: {
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 24,
        paddingRight: 24,
    },
    isolateModalContent: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
        borderBottomLeftRadius: 18,
        borderBottomRightRadius: 18,
        overflow: 'hidden',
    },
    // NOTE: you can find this in App.css; search: modalFadeIn
    fadeAnimation: {
        animation: `modalFadeIn ${MODAL_ANIMATION_DURATION}ms ease-out forwards`,
    },
    contentAnimation: {
        animation: `modalContentFadeIn ${MODAL_ANIMATION_DURATION}ms ease-out forwards`,
    },
    fadeOutAnimation: {
        animation: `modalFadeOut ${MODAL_ANIMATION_DURATION}ms ease-out forwards`,
    },
    contentFadeOutAnimation: {
        animation: `modalContentFadeOut ${MODAL_ANIMATION_DURATION}ms ease-out forwards`,
    },
    modalSymphonyChildren: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
    },
    modalSymphonyButtonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'right',
        alignItems: 'center',
        width: '100%',
        gap: 16,
        marginTop: 12
    },
    leftSideContentContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        marginTop: 'auto',
        marginBottom: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
    },

    modalSymphonyLeftSideContent: {
        display: 'flex',
        flexDirection: 'column',
        // alignItems: 'center',
        // justifyContent: 'center',
        borderTopLeftRadius: 18,
        borderBottomLeftRadius: 18,
        backgroundColor: 'white',
    }
}

export default ModalSymphony;
